import { defineStore } from 'pinia'

import type {
	IFetchApiGetNotifications,
	IFetchApiReadAllNotifications,
	IFetchApiReadNotification
} from '~/portal/api'
import {
	fetchApiGetNotifications,
	fetchApiReadAllNotifications,
	fetchApiReadNotification
} from '~/portal/api'
import type {
	INotificationsCountData,
	INotificationsStoreTypes
} from '~/portal/stores'
import { NotificationTypeEnum, useChatStore } from '~/portal/stores'

export const useNotificationsStore = defineStore('notificationsStore', {
	state: (): INotificationsStoreTypes => ({
		currentPage: 1,
		isPagination: false,
		notifications: [],
		notificationCount: {
			ALL: 0,
			PROJECT: 0,
			LEGAL: 0,
			BILLING: 0,
			OPERATIONAL: 0
		}
	}),
	actions: {
		resetPagination () {
			this.currentPage = 1
			this.isPagination = false
			this.notifications = []
		},

		async getNotificationsCount (token: string) {
			const request = Object.keys(this.notificationCount).map((type) => {
				return fetchApiGetNotifications({
					token,
					forceFetch: true,
					params: {
						is_seen: false,
						type: type === NotificationTypeEnum.All
							? undefined
							: type
					}
				})
			})

			const responses = await Promise.all(request)

			if (responses) {
				responses.forEach((response, index) => {
					const key = Object.keys(this.notificationCount)[index] as keyof INotificationsCountData
					this.notificationCount[key] = response?.count ?? 0
				})
			}
		},

		async getNotificationList (payload: IFetchApiGetNotifications) {
			const response = await fetchApiGetNotifications({
				...payload,
				params: {
					...payload.params,
					page: this.currentPage
				}
			})
			if (response?.results) {
				this.currentPage = Number(response.next) ?? Number(response.previous ?? 0) + 1
				this.notifications?.push(...response.results)
				this.isPagination = !!response.next
			}
		},

		async readNotification (payload: IFetchApiReadNotification) {
			const response = await fetchApiReadNotification(payload)

			if (response) {
				const item = this.notifications.find((item) => {
					return item.id === payload.id
				})

				if (item && !item.is_seen) {
					const { decreaseNotificationCount } = useChatStore()

					item.is_seen = true
					this.notificationCount.ALL -= 1
					this.notificationCount[item.type] -= 1
					decreaseNotificationCount()
				}
			}
		},

		async readAllNotifications (payload: IFetchApiReadAllNotifications) {
			const response = await fetchApiReadAllNotifications(payload)

			if (response) {
				const { fetchApiChatAllMessageCount } = useChatStore()
				this.resetPagination()

				await Promise.all([
					this.getNotificationsCount(payload.token),
					this.getNotificationList({
						...payload,
						params: {
							...payload.params,
							page: 1
						}
					}),
					fetchApiChatAllMessageCount({ token: payload.token })
				])
			}
		}
	}
})
